'use client';

// import { Comment } from '@prisma/client';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import { Comment } from '../../../types/shared';

type BasePayload = {
  placeId?: string;
  suggestionId?: string;
};

export type GlobalRealtimeEvent =
  | { type: 'VOTE_UPDATE'; payload: BasePayload & { likes: number; dislikes: number } }
  | { type: 'NEW_COMMENT'; payload: BasePayload & Comment }
  | { type: 'DELETE_COMMENT'; payload: BasePayload & { commentId: string; commentIds?: string[] } };


type Listener = () => void;

function getPayloadId(e: GlobalRealtimeEvent): string | undefined {
  return e.payload?.placeId || e.payload?.suggestionId;
}

// ───────────────── Store ─────────────────

const store = {
  isConnected: false,

  // слушатели по конкретному itemId
  itemListeners: new Map<string, Set<Listener>>(),

  // последнее событие по itemId (для snapshot)
  lastByItem: new Map<string, GlobalRealtimeEvent>(),

  // слушатели статуса соединения (редко меняется)
  connListeners: new Set<Listener>(),

  setConnected(v: boolean) {
    if (this.isConnected === v) return;
    this.isConnected = v;
    for (const l of this.connListeners) l();
  },

  publish(event: GlobalRealtimeEvent) {
    const id = getPayloadId(event);
    if (!id) return;

    this.lastByItem.set(id, event);

    const set = this.itemListeners.get(id);
    if (!set) return;

    for (const l of set) l();
  },

  subscribeItem(id: string, listener: Listener) {
    let set = this.itemListeners.get(id);
    if (!set) {
      set = new Set<Listener>();
      this.itemListeners.set(id, set);
    }
    set.add(listener);
  
    return () => {
      const s = this.itemListeners.get(id);
      if (!s) return;
  
      s.delete(listener);
  
      if (s.size === 0) {
        this.itemListeners.delete(id);
        this.lastByItem.delete(id); // ✅ вот это убирает “память навсегда”
      }
    };
  },

  getItemSnapshot(id: string) {
    return this.lastByItem.get(id) ?? null;
  },

  subscribeConn(listener: Listener) {
    this.connListeners.add(listener);
    return () => this.connListeners.delete(listener);
  },

  getConnSnapshot() {
    return this.isConnected;
  },
};

const TourismGlobalContext = createContext<{ _ready: true } | null>(null);

export function TourismGlobalEventsProvider({ children }: { children: ReactNode }) {
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (esRef.current) return; // ✅ вместо readyState === 1 (0 тоже “живой”)
  
    const es = new EventSource('/api/tourism/events');
    esRef.current = es;
  
    es.onopen = () => store.setConnected(true);
  
    es.onmessage = (event) => {
      try {
        if (!event.data || event.data === ': ping') return;
        const data: GlobalRealtimeEvent = JSON.parse(event.data);
        store.publish(data);
      } catch (err) {
        console.error('SSE Parse Error:', err);
      }
    };
  
    es.onerror = () => store.setConnected(false);
  
    return () => {
      es.close();
      esRef.current = null;
      store.setConnected(false);
    };
  }, []);
  

  return (
    <TourismGlobalContext.Provider value={{ _ready: true }}>
      {children}
    </TourismGlobalContext.Provider>
  );
}

// ✅ Подписка только на события конкретного itemId
export function useTourismItemEvents(itemId: string) {
  const ctx = useContext(TourismGlobalContext);
  if (!ctx) throw new Error('useTourismItemEvents must be used within TourismGlobalEventsProvider');

  const lastEvent = useSyncExternalStore(
    (cb) => store.subscribeItem(itemId, cb),
    () => store.getItemSnapshot(itemId),
    () => store.getItemSnapshot(itemId),
  );

  return lastEvent;
}

// (опционально) статус соединения — если надо рисовать “онлайн/оффлайн”
export function useTourismEventsConnection() {
  const ctx = useContext(TourismGlobalContext);
  if (!ctx)
    throw new Error('useTourismEventsConnection must be used within TourismGlobalEventsProvider');

  return useSyncExternalStore(
    store.subscribeConn.bind(store),
    store.getConnSnapshot.bind(store),
    store.getConnSnapshot.bind(store),
  );
}

// Для совместимости со старым кодом (если где-то ещё импортируешь)
// Можно удалить позже.
export function useTourismGlobalEvents() {
  return { lastEvent: null, isConnected: useTourismEventsConnection() };
}
